import { Branch, Franchisee, FranchiseeConfiguration, TaxRate } from '../../api/dealer-api-interface-franchisee';
import { claimIdentity, userSecurity } from '../../../webmodule-common/other/api/user-security';
import {
  ConnectionState,
  GlobalNotificationManager
} from '../../../webmodule-common/other/ui/icons/icon-notification-signal';
import { currentQuoteSupplierProvider } from '../../quotes/data/quoteSupplierProvider';
import { DevelopmentError } from '../../../webmodule-common/other/development-error';
import { displaySupplierTACNotApprovedMsg } from '../../v6config/supplier-services';
import { emptyGuid } from '../../../webmodule-common/other/api/guid';
import { flushCache } from '../cache-impl/cache-registry';
import {
  FranchiseeConfigData,
  IFranchiseeDataAdaptor,
  setFranchiseeDataAdaptor
} from '../../franchisee/data/franchisee-data-adaptor';
import { getApiFactory } from '../../api/api-injector';
import { getCurrentUser, setUserClaims } from '../../../webmodule-common/other/api/current-user';
import { getSupplier } from '../quotes/supplier-selector';
import { isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';
import { isMultiSupplier } from '../../quotes/quote-service';
import { MultiPromise } from '../../../webmodule-common/other/general/multicast-promise';
import { NullPromise } from '../../../webmodule-common/interop/types/misc';
import { setSupplierReferenceOverride } from './v6/supplier-reference-override';
import { updateCurrentUserClaims } from '../../../webmodule-common/other/currentuser-claims';

const branchIdKey = 'webmodule-branchid';

export class CurrentUserDataStore implements IFranchiseeDataAdaptor {
  //IFranchiseeDataAdaptor
  loaded = false;
  private _v6OverrideKey?: string;
  private _systemDefaultBranchId: string = emptyGuid;
  private _branches: Branch[] | null = null;
  private _defaultBranchId = '';
  private _franchiseeConfig: FranchiseeConfiguration | null = null;
  private loadCorePromise = new MultiPromise<void>(async () => await this.performLoadCoreDetails(), false);
  private _wasOffline = false;
  private _connectionStateUnsubscribe: (() => void) | null = null;

  public get allowAllBranchAccess(): boolean {
    return userSecurity().claimIsTrue(claimIdentity.allBranches);
  }

  private _taxRates: TaxRate[] | null = null;

  public get taxRates(): TaxRate[] {
    this.checkLoaded();
    if (!this._taxRates) throw new DevelopmentError('Franchisee rates are null');
    return this._taxRates;
  }

  private _franchisee: Franchisee | null = null;

  public get franchisee(): Franchisee {
    this.checkLoaded();
    if (!this._franchisee) throw new DevelopmentError('Franchisee is null');
    return this._franchisee;
  }

  public get defaultTaxRate(): number {
    if (this._taxRates && this._taxRates.length > 0) return this._taxRates[0].rate;
    return 0;
  }

  public get config(): FranchiseeConfiguration {
    this.checkLoaded();
    if (!this._franchiseeConfig) throw new DevelopmentError('Franchisee is null');
    return this._franchiseeConfig;
  }

  get clientOwnerId() {
    return this.defaultBranch.id;
  }

  public get defaultBranch(): Branch {
    this.checkLoaded();

    const branch = this._branches?.find(x => x.id == this._defaultBranchId) ?? null;
    if (!branch) throw new DevelopmentError('Default Branch is null');
    return branch;
  }

  public set defaultBranch(value: Branch) {
    const b = this._branches?.find(x => x.id === value.id);
    //cannot reset default branch without this privelage
    if (!this.allowAllBranchAccess || !b) {
      this.internalSetDefaultBranch(this._systemDefaultBranchId);
      return;
    }
    this.internalSetDefaultBranch(b.id);
  }

  public get quoteSupplierProvider(): string {
    return currentQuoteSupplierProvider;
  }

  public async setV6OverrideKey() {
    const aKey = this._defaultBranchId;
    if (aKey !== this._v6OverrideKey) {
      await setSupplierReferenceOverride(aKey);
      this._v6OverrideKey = aKey;
      return true;
    }
    return false;
  }

  async singleSupplierTACApproved(): Promise<boolean> {
    if (isMultiSupplier()) return true;
    const supplier = await getSupplier();
    if (isEmptyOrSpace(supplier.supplierId)) return false;
    if (!this.supplierTACApproved(supplier.supplierId)) {
      await displaySupplierTACNotApprovedMsg(supplier, true);
      return false;
    }
    return true;
  }

  hasBranch(branchId: string): boolean {
    return this.allowAllBranchAccess && this._branches?.find(x => x.id === branchId) !== undefined;
  }

  //IFranchiseeDataAdaptor
  getFranchisee(): Franchisee {
    return this.franchisee;
  }

  public supplierTACApproved(supplierId: string): boolean {
    this.checkLoaded();
    const key = `supplier-${supplierId}-tac`;
    return this.franchisee.properties[key] === 'true';
  }

  async setFranchisee(franchisee: Franchisee): NullPromise<Franchisee> {
    const result = await getApiFactory().franchisee().updateFranchisee({ franchisee: franchisee });
    if (result && franchisee.id === this.franchisee.id) {
      this._franchisee = result.franchisee;
    }
    return result?.franchisee ?? null;
  }

  getFranchiseeConfig(): FranchiseeConfigData {
    return {
      franchiseeConfiguration: this.config,
      taxRates: this.taxRates
    };
  }

  async setFranchiseeConfig(franchiseeConfig: FranchiseeConfigData): NullPromise<FranchiseeConfigData> {
    const result = await getApiFactory().franchisee().updateFranchiseeConfiguration({
      franchiseeConfiguration: franchiseeConfig.franchiseeConfiguration,
      taxRates: franchiseeConfig.taxRates
    });
    if (result) {
      this._franchiseeConfig = result.franchiseeConfiguration;
      this._taxRates = result.taxRates;
      return {
        franchiseeConfiguration: result.franchiseeConfiguration,
        taxRates: result.taxRates
      };
    }
    return null;
  }

  getUserBranches(): Branch[] {
    this.checkLoaded();
    return this._branches ?? [];
  }

  async getAllBranches(): Promise<Branch[]> {
    return (await getApiFactory().franchisee().getBranches())?.branches ?? [];
  }

  async setBranch(branch: Branch): Promise<Branch | null> {
    const api = getApiFactory().franchisee();
    const branchResult =
      branch.id === emptyGuid
        ? await api.createBranch({
            abbreviation: branch.abbreviation,
            taxRegistrationNumber: branch.taxRegistrationNumber,
            billingAddress: branch.billingAddress,
            name: branch.name,
            physicalAddress: branch.physicalAddress,
            physicalAddressAsDefaultShipping: branch.physicalAddressAsDefaultShipping,
            shippingNotes: branch.shippingNotes,
            id: null
          })
        : await api.updateBranch({
            branch: branch
          });
    if (branchResult && this._branches) {
      const idx = this._branches.findIndex(x => x.id === branchResult.id);

      if (idx > -1) {
        this._branches[idx] = branchResult;
      } else if (this.allowAllBranchAccess) this._branches.push(branchResult);
      return branchResult;
    }

    return null;
  }

  public branchById(id: string): Branch {
    if (!this._branches) throw new DevelopmentError('load branches before trying to access them');
    const b = this._branches.find(x => x.id === id);
    if (!b) throw new Error(`Branch ${id} does not exist`);
    return b;
  }

  /**
   * This should only be called from LogOFF
   */
  clear() {
    this.loaded = false;
    this.loadCorePromise.reset();

    this._franchisee = null;
    this._branches = null;
    this._defaultBranchId = '';
    this._franchiseeConfig = null;
    this._systemDefaultBranchId = emptyGuid;
    this._taxRates = null;
    localStorage.removeItem(branchIdKey);
    //        this._userClaims = null;

    // Unsubscribe from connection state changes
    if (this._connectionStateUnsubscribe) {
      this._connectionStateUnsubscribe();
      this._connectionStateUnsubscribe = null;
    }
    this._wasOffline = false;

    flushCache();
  }

  /**
   * This should called so the next await gets new data, without destroying in use data
   */
  requestRefresh() {
    this.loadCorePromise.reset();
  }

  /**
   * forces the cache to refresh
   * @returns a fresh copy of the franchisee
   */
  async reLoadFranchisee(): NullPromise<Franchisee> {
    this.requestRefresh();
    await this.loadCoreDetails();
    return this.franchisee;
  }

  async loadCoreDetailsAfterLogin() {
    await this.loadCorePromise.run();
    this.subscribeToConnectionState();
  }

  async loadCoreDetails() {
    await this.loadCorePromise.run();
  }

  /**
   * Subscribe to connection state changes to refresh user claims when coming back online.
   */
  private subscribeToConnectionState() {
    if (this._connectionStateUnsubscribe) return; // Already subscribed

    const manager = GlobalNotificationManager.getInstance();
    this._wasOffline = !manager.online;

    this._connectionStateUnsubscribe = manager.onConnectionStateChange(async state => {
      if (state === ConnectionState.Online && this._wasOffline) {
        console.log('Connection restored - refreshing user claims');
        this.requestRefresh();
        await this.loadCoreDetails();
      }
      this._wasOffline = state === ConnectionState.Offline || state === ConnectionState.ServiceRestarting;
    });
  }

  configureDefaultBranch(defaultBranchId: string): string {
    if (!this.allowAllBranchAccess) {
      localStorage.setItem(branchIdKey, defaultBranchId);
      return defaultBranchId;
    }

    const lastSelectedBranchId = localStorage.getItem(branchIdKey) ?? emptyGuid;
    const lastBranch = this._branches?.find(x => x.id === lastSelectedBranchId);
    return lastBranch?.id ?? defaultBranchId;
  }

  private internalSetDefaultBranch(id: string) {
    if (id === this._defaultBranchId) return;
    let b = this.branchById(id);
    if (!b) {
      b = this.branchById(this._systemDefaultBranchId);
    }
    if (b) {
      this._defaultBranchId = id;
      localStorage.setItem(branchIdKey, id);
      flushCache();
    }
  }

  private checkLoaded() {
    if (!this.loaded) throw new Error('CurrentUserDataStore has not been loaded');
  }

  private async performLoadCoreDetails() {
    const user = getCurrentUser();
    if (!user) throw new DevelopmentError('Authentication could not be performed');

    const api = getApiFactory().franchisee();
    const result = await api.getActiveUserInformation({
      systemUserId: user.id
    });
    if (result) {
      this._franchisee = result.franchisee;
      this._branches = result.branches;
      this._franchiseeConfig = result.franchiseeConfiguration;
      this._taxRates = result.taxRates;
      this._systemDefaultBranchId = result.defaultBranchId;
      setUserClaims(result.userClaims);
      updateCurrentUserClaims();
      this._defaultBranchId = this.configureDefaultBranch(result.defaultBranchId);
      this.loaded = true; // set before accessing V6

      setFranchiseeDataAdaptor(this);
    } else throw new Error('could not load user details');
  }
}

export const userDataStore = new CurrentUserDataStore();
